import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, FileText, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import html2pdf from 'html2pdf.js';
import { periodInvoiceItems } from '../../../supabase/functions/_shared/lease-lines.ts';
import { indexationNote } from '../../../supabase/functions/_shared/indexation.ts';
import { leasePaymentDetails } from '../../../supabase/functions/_shared/payment-account.ts';
import { quittanceIssueDate } from '../../../supabase/functions/_shared/invoice-template.ts';

interface ReceiptDetailDialogProps {
  receiptId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatSiret(siret: string): string {
  const clean = siret.replace(/\s/g, '');
  if (clean.length === 14) {
    return clean.slice(0, 3) + ' ' + clean.slice(3, 6) + ' ' + clean.slice(6, 9) + ' ' + clean.slice(9);
  }
  return siret;
}

function formatIban(iban: string): string {
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

function generatePreviewHtml(receipt: any, depositItems: any[] = []): string {
  const tenant = receipt.rent_periods?.leases?.tenants;
  const property = receipt.rent_periods?.leases?.properties;
  const sci = property?.scis;
  const paymentAccount = leasePaymentDetails(receipt.rent_periods?.leases, sci);
  const rentPeriod = receipt.rent_periods;
  const lease = receipt.rent_periods?.leases;

  const vatRate = parseFloat(lease?.vat_rate) || 0;
  // rent_due / charges_due are stored TTC; the document shows the contract HT amounts.
  const rentHT = parseFloat(lease?.rent_amount) || 0;
  const chargesHT = parseFloat(lease?.charges_amount) || 0;
  // Deposit is TVA-exempt - sum all deposit items
  const depositTotal = depositItems.reduce((sum, item) => sum + (parseFloat(item.amount_ttc) || 0), 0);
  const periodItems = periodInvoiceItems(lease, rentPeriod);
  const leaseLines = !!periodItems;
  const lineItems = periodItems ?? [];
  const lineDeposit = lineItems.filter(i => i.type === 'deposit').reduce((s, i) => s + i.amountHT, 0);
  const lineTotals = periodItems ? {
    ht: lineItems.reduce((s, i) => s + i.amountHT, 0) - lineDeposit,
    vat: lineItems.reduce((s, i) => s + i.amountTTC - i.amountHT, 0),
  } : null;
  const lineRates = [...new Set(lineItems.filter(i => i.vatRate > 0).map(i => i.vatRate))];
  const vatLabel = leaseLines ? (lineRates.length ? ` (${lineRates.join('% / ')}%)` : '') : (vatRate ? ` (${vatRate}%)` : '');
  const subTotalBeforeDeposit = lineTotals ? lineTotals.ht : rentHT + chargesHT;
  const vatAmount = lineTotals ? lineTotals.vat : (vatRate ? subTotalBeforeDeposit * (vatRate / 100) : 0);
  const subTotal = subTotalBeforeDeposit + depositTotal + lineDeposit;
  const total = subTotal + vatAmount;
  const note = indexationNote(rentPeriod?.lease_indexations, lease);
  const isPaid = rentPeriod?.status === 'paid';

  const tenantName = (tenant?.tenant_type === 'professional' || tenant?.tenant_type === 'company')
    ? (tenant?.company_name || `${tenant?.first_name || ''} ${tenant?.last_name || ''}`.trim())
    : `${tenant?.first_name || ''} ${tenant?.last_name || ''}`.trim();

  const isProfessional = tenant?.tenant_type === 'professional' || tenant?.tenant_type === 'company';

  const periodStr = rentPeriod?.period_yyyymm || '';
  let formattedPeriod = '';
  if (periodStr) {
    const [year, month] = periodStr.split('-');
    formattedPeriod = format(new Date(parseInt(year), parseInt(month) - 1), 'MMMM yyyy', { locale: fr });
    formattedPeriod = formattedPeriod.charAt(0).toUpperCase() + formattedPeriod.slice(1);
  }

  const issuerAddress = [sci?.address, sci?.postal_code, sci?.city].filter(Boolean).join(', ');
  const clientAddress = [tenant?.adresse_locataire, tenant?.code_postal_locataire, tenant?.ville_locataire].filter(Boolean).join(', ');
  const propertyAddress = [property?.address, property?.postal_code, property?.city].filter(Boolean).join(', ');
  const formattedSiret = sci?.siret ? formatSiret(sci.siret) : '';
  const formattedIban = paymentAccount.iban ? formatIban(paymentAccount.iban) : '';

  const documentTitle = isPaid ? 'QUITTANCE DE LOYER' : 'FACTURE';
  const issueDate = quittanceIssueDate(receipt.generated_at, periodStr);
  const dueDate = periodStr ? `05/${periodStr.split('-')[1]}/${periodStr.split('-')[0]}` : '';

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>${documentTitle} ${receipt.receipt_number}</title>
  <style>
    @page { size: A4; margin: 15mm; }
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 11px; color:#1f2937; padding: 20px 30px; background: white; line-height: 1.4; }
    .container { max-width: 100%; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 3px solid #1f2937; margin-bottom: 20px; }
    .issuer { width: 55%; }
    .issuer .name { font-size: 18px; font-weight: 700; color: #111827; margin-bottom: 8px; }
    .issuer .meta { color: #4b5563; font-size: 10px; line-height: 1.5; }
    .issuer .meta div { margin-bottom: 2px; }
    .doc-info { width: 40%; text-align: right; }
    .doc-info .type { font-size: 20px; font-weight: 800; letter-spacing: 1px; color: #111827; margin-bottom: 12px; }
    .doc-info .details { font-size: 11px; color: #374151; line-height: 1.6; }
    .doc-info .details strong { color: #111827; }
    .parties { display: flex; justify-content: space-between; gap: 20px; margin-bottom: 25px; }
    .party-box { width: 48%; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 16px; }
    .party-box h3 { font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: #6b7280; margin-bottom: 10px; font-weight: 600; }
    .party-box .name { font-size: 13px; font-weight: 700; color: #111827; margin-bottom: 6px; }
    .party-box .detail { font-size: 10px; color: #4b5563; margin-bottom: 3px; }
    .property-box { background: #fef3c7; border: 1px solid #fcd34d; border-radius: 6px; padding: 12px 16px; margin-bottom: 20px; }
    .property-box .label { font-size: 9px; text-transform: uppercase; color: #92400e; font-weight: 600; letter-spacing: 1px; }
    .property-box .value { font-size: 12px; font-weight: 600; color: #78350f; margin-top: 4px; }
    table.items { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    table.items th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; padding: 10px 12px; background: #f3f4f6; border-bottom: 2px solid #d1d5db; font-weight: 600; }
    table.items td { padding: 12px; border-bottom: 1px solid #e5e7eb; color: #374151; font-size: 11px; }
    table.items .num, table.items th.num { text-align: right; }
    .totals-wrapper { display: flex; justify-content: flex-end; margin-bottom: 20px; }
    table.totals { width: 280px; }
    table.totals td { padding: 8px 12px; font-size: 11px; }
    table.totals .label { color: #6b7280; }
    table.totals .value { text-align: right; font-weight: 500; }
    table.totals .grand td { font-size: 14px; font-weight: 700; color: #111827; border-top: 2px solid #111827; padding-top: 12px; }
    table.totals .grand.paid td { color: #16a34a; }
    .payment-box { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 6px; padding: 16px; margin-bottom: 20px; }
    .payment-box.paid { background: #f0fdf4; border-color: #86efac; }
    .payment-box h4 { font-size: 11px; font-weight: 700; color: #0369a1; margin-bottom: 8px; }
    .payment-box.paid h4 { color: #15803d; }
    .payment-box p { font-size: 10px; color: #334155; line-height: 1.5; margin: 0; }
    .payment-box .iban { background: white; padding: 8px 12px; border-radius: 4px; font-family: monospace; font-size: 11px; margin-top: 10px; border: 1px solid #e2e8f0; }
    .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e5e7eb; font-size: 9px; color: #6b7280; line-height: 1.5; }
    .footer .legal { margin-bottom: 10px; }
    .footer .mentions { background: #f9fafb; padding: 10px; border-radius: 4px; font-size: 8px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="issuer">
        ${sci?.logo_url ? `<img style="max-height:60px;margin-bottom:12px" src="${sci.logo_url}" alt="Logo" />` : ''}
        <div class="name">${sci?.name || 'SCI'}</div>
        <div class="meta">
          <div>${issuerAddress}</div>
          ${formattedSiret ? `<div><strong>SIRET :</strong> ${formattedSiret}</div>` : ''}
          ${sci?.vat_number ? `<div><strong>N° TVA Intracommunautaire :</strong> ${sci.vat_number}</div>` : ''}
        </div>
      </div>
      <div class="doc-info">
        <div class="type">${documentTitle}</div>
        <div class="details">
          <div><strong>N° Document :</strong> ${receipt.receipt_number}</div>
          <div><strong>Date d'émission :</strong> ${issueDate}</div>
          ${dueDate ? `<div><strong>Date d'échéance :</strong> ${dueDate}</div>` : ''}
          <div><strong>Période :</strong> ${formattedPeriod}</div>
        </div>
      </div>
    </div>
    <div class="parties">
      <div class="party-box">
        <h3>Émetteur / Bailleur</h3>
        <div class="name">${sci?.name || 'SCI'}</div>
        <div class="detail">${issuerAddress}</div>
        ${formattedSiret ? `<div class="detail">SIRET : ${formattedSiret}</div>` : ''}
        ${sci?.vat_number ? `<div class="detail">N° TVA Intracommunautaire : ${sci.vat_number}</div>` : ''}
      </div>
      <div class="party-box">
        <h3>Destinataire / Locataire</h3>
        <div class="name">${tenantName}</div>
        ${clientAddress ? `<div class="detail">${clientAddress}</div>` : ''}
        ${isProfessional && tenant?.siret ? `<div class="detail">SIRET : ${formatSiret(tenant.siret)}</div>` : ''}
        ${isProfessional && tenant?.vat_number ? `<div class="detail">N° TVA : ${tenant.vat_number}</div>` : ''}
        ${tenant?.email ? `<div class="detail">${tenant.email}</div>` : ''}
      </div>
    </div>
    <div class="property-box">
      <div class="label">Bien concerné</div>
      <div class="value">${property?.name || 'Bien'}${propertyAddress ? ` — ${propertyAddress}` : ''}</div>
    </div>
    <table class="items">
      <thead>
        <tr>
          <th>Désignation</th>
          <th class="num">Qté</th>
          <th class="num">P.U. HT (€)</th>
          <th class="num">Taux de TVA</th>
          <th class="num">Total HT (€)</th>
        </tr>
      </thead>
      <tbody>
        ${leaseLines ? lineItems.map(item => `
        <tr>
          <td>${item.label}${item.annotation ? `<br/><span style="font-size:11px;color:#1f4e79;font-style:italic">${item.annotation}</span>` : ''}${propertyAddress ? `<br/><span style="font-size:11px;color:#6b7280">${propertyAddress}</span>` : ''}</td>
          <td class="num">1</td>
          <td class="num">${item.amountHT.toFixed(2)}</td>
          <td class="num">${item.vatRate ? item.vatRate + '%' : '-'}</td>
          <td class="num">${item.amountHT.toFixed(2)}</td>
        </tr>
        `).join('') : `
        <tr>
          <td>Loyer — ${property?.name || 'Bien'}${propertyAddress ? `<br/><span style="font-size:11px;color:#6b7280">${propertyAddress}</span>` : ''}</td>
          <td class="num">1</td>
          <td class="num">${rentHT.toFixed(2)}</td>
          <td class="num">${vatRate ? vatRate + '%' : '-'}</td>
          <td class="num">${rentHT.toFixed(2)}</td>
        </tr>
        ${chargesHT > 0 ? `
        <tr>
          <td>Charges locatives — ${property?.name || 'Bien'}${propertyAddress ? `<br/><span style="font-size:11px;color:#6b7280">${propertyAddress}</span>` : ''}</td>
          <td class="num">1</td>
          <td class="num">${chargesHT.toFixed(2)}</td>
          <td class="num">${vatRate ? vatRate + '%' : '-'}</td>
          <td class="num">${chargesHT.toFixed(2)}</td>
        </tr>
        ` : ''}
        `}
        ${depositItems.map(item => `
        <tr>
          <td>${item.label || 'Dépôt de garantie'}</td>
          <td class="num">1</td>
          <td class="num">${parseFloat(item.amount_ttc).toFixed(2)}</td>
          <td class="num">-</td>
          <td class="num">${parseFloat(item.amount_ttc).toFixed(2)}</td>
        </tr>
        `).join('')}
      </tbody>
    </table>
    <div class="totals-wrapper">
      <table class="totals">
        <tr><td class="label">${lineDeposit ? 'Total loyers HT' : 'Sous-total HT (loyer + charges)'}</td><td class="value">${subTotalBeforeDeposit.toFixed(2)} €</td></tr>
        <tr><td class="label">TVA${vatLabel}</td><td class="value">${vatAmount.toFixed(2)} €</td></tr>
        ${depositTotal > 0 ? `<tr><td class="label">Dépôt de garantie (exonéré TVA)</td><td class="value">${depositTotal.toFixed(2)} €</td></tr>` : ''}
        ${lineDeposit ? `<tr><td class="label">Total loyers TTC</td><td class="value">${(subTotalBeforeDeposit + vatAmount).toFixed(2)} €</td></tr>
        <tr><td class="label">Ajustement dépôt de garantie</td><td class="value">${lineDeposit.toFixed(2)} €</td></tr>` : ''}
        <tr class="grand">
          <td>${lineDeposit ? 'Total à régler' : 'Total TTC à régler'}</td>
          <td class="value">${total.toFixed(2)} €</td>
        </tr>
      </table>
    </div>
    ${note ? `<div style="background:#eaf1f8;border-left:3px solid #1f4e79;padding:12px 14px;margin-bottom:20px;font-size:11px;color:#1c2430">${note}</div>` : ''}
    <div class="payment-box">
      <h4>Modalités de règlement</h4>
      <p>Montant à régler : <strong>${total.toFixed(2)} €</strong> au titre du loyer et des charges pour la période de <strong>${formattedPeriod}</strong>${dueDate ? `, avant le <strong>${dueDate}</strong>` : ''}.
        ${paymentAccount.iban ? `<br/>Paiement par virement bancaire :
          <div class="iban"><strong>Titulaire :</strong> ${sci?.name || 'SCI'}<br/><strong>IBAN :</strong> ${formattedIban}${paymentAccount.bic ? `<br/><strong>BIC :</strong> ${paymentAccount.bic}` : ''}</div>
        ` : ''}
        <br/>Nous vous remercions par avance pour votre règlement.
      </p>
    </div>
    <div class="footer">
      <div class="mentions">
        <strong>Document émis par :</strong> ${sci?.name || 'SCI'}
        ${formattedSiret ? ` — SIRET : ${formattedSiret}` : ''}
        ${sci?.vat_number ? ` — TVA : ${sci.vat_number}` : ''}
      </div>
    </div>
  </div>
</body>
</html>`;
}

export async function fetchReceiptForPdf(receiptId: string) {
  const { data, error } = await supabase
    .from('receipts')
    .select(`
      *,
      rent_periods(
        id, period_yyyymm, rent_due, charges_due, total_due, total_paid, due_date, status, invoice_lines,
        lease_indexations!rent_periods_indexation_id_fkey(*),
        leases(
          sci_payment_accounts!leases_payment_account_id_fkey(iban, bic),
          rent_amount, charges_amount, vat_rate, rent_lines, indexation_invoice_month, indexation_effective_month,
          properties(
            name, address, city, postal_code, vat_rate,
            scis(name, siret, vat_number, address, city, postal_code, iban, bic, signature_name, logo_url)
          ),
          tenants(first_name, last_name, email, tenant_type, company_name, adresse_locataire, code_postal_locataire, ville_locataire, siret, vat_number)
        )
      )
    `)
    .eq('id', receiptId)
    .single();
  if (error) throw error;
  return data;
}

export function downloadReceiptPdf(receipt: any, html = generatePreviewHtml(receipt)): Promise<void> {
  const opt: any = {
    margin: 10,
    filename: `${receipt.receipt_number}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
  };
  const element = document.createElement('div');
  element.innerHTML = html;
  return html2pdf().set(opt).from(element).save();
}

export function ReceiptDetailDialog({ receiptId, open, onOpenChange }: ReceiptDetailDialogProps) {
  const { data: receipt, isLoading } = useQuery({
    queryKey: ['receipt-preview', receiptId],
    queryFn: async () => (receiptId ? fetchReceiptForPdf(receiptId) : null),
    enabled: !!receiptId && open,
  });

  // Deposits were previously carried on the (now removed) appel de fonds line items.
  const depositItems: Array<{ label: string; amount_ht: number; amount_ttc: number; vat_rate: number; item_type: string }> = [];

  const html = useMemo(() => {
    if (!receipt) return null;
    return generatePreviewHtml(receipt, depositItems);
  }, [receipt, depositItems]);

  const handleDownload = () => {
    if (!html || !receipt) return;
    downloadReceiptPdf(receipt, html);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[95vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {receipt ? `Aperçu — ${receipt.receipt_number}` : 'Aperçu de la quittance'}
            </DialogTitle>
            {html && (
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Télécharger
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 px-6 pb-6">
          {isLoading && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Chargement...
            </div>
          )}

          {!isLoading && !receipt && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              Quittance introuvable
            </div>
          )}

          {html && (
            <iframe
              srcDoc={html}
              title="Aperçu quittance"
              className="w-full h-[75vh] border rounded-lg bg-white"
              sandbox="allow-same-origin"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
